import { AxiosError, AxiosResponse } from 'axios';
import instance, { ApiError } from '../../api/instance';
import { DeviceGroup, GroupDevice } from './deviceGroups.types';
import { Firmware } from 'src/features/firmwares/firmwares.types';

const deviceGroupsApiPath = 'device_groups';

export type DeviceGroupQueryParams = {
  name?: string;
};

export const getDeviceGroupsAPI = async ({
  params,
}: {
  params?: DeviceGroupQueryParams;
}): Promise<AxiosResponse<DeviceGroup[] & ApiError>> => {
  return instance
    .get<DeviceGroup[]>(deviceGroupsApiPath, { params })
    .catch(e => e.response as AxiosError<ApiError> as never);
};

export const createDeviceGroupAPI = async ({
  name,
}: {
  name: string;
}): Promise<AxiosResponse<DeviceGroup & ApiError>> => {
  return instance
    .post<DeviceGroup>(deviceGroupsApiPath, { name: name })
    .catch(e => e.response as AxiosError<ApiError> as never);
};

const deviceGroupApiPath = (id: string) => `/device_groups/${id}`;

export const getDeviceGroupAPI = async ({
  groupId,
}: {
  groupId: string;
}): Promise<AxiosResponse<DeviceGroup & ApiError>> => {
  return instance.get<DeviceGroup>(deviceGroupApiPath(groupId)).catch(e => e.response as AxiosError<ApiError> as never);
};

export const updateDeviceGroupAPI = async ({
  groupId,
  data,
}: {
  groupId: string;
  data: Partial<Pick<DeviceGroup, 'name' | 'templateId' | 'deviceScreens'>>;
}): Promise<AxiosResponse<DeviceGroup & ApiError>> => {
  return instance
    .post<DeviceGroup>(deviceGroupApiPath(groupId), data)
    .catch(e => e.response as AxiosError<ApiError> as never);
};

export const deleteDeviceGroupAPI = async ({
  groupId,
}: {
  groupId: string;
}): Promise<AxiosResponse<DeviceGroup & ApiError>> => {
  return instance
    .delete<DeviceGroup>(deviceGroupApiPath(groupId))
    .catch(e => e.response as AxiosError<ApiError> as never);
};

const groupDevicesApiPath = (id: string) => `/device_groups/${id}/devices`;

export const getGroupDevicesAPI = async ({
  groupId,
  params,
}: {
  groupId: string;
  params?: { search?: string };
}): Promise<AxiosResponse<GroupDevice[] & ApiError>> => {
  return instance
    .get<GroupDevice[]>(groupDevicesApiPath(groupId), { params })
    .catch(e => e.response as AxiosError<ApiError> as never);
};

type DeviceIds = {
  deviceId: string;
};
export const addDevicesToGroupAPI = async ({
  groupId,
  data,
}: {
  groupId: string;
  data: DeviceIds;
}): Promise<AxiosResponse<GroupDevice[] & ApiError>> => {
  return instance
    .post<GroupDevice[]>(groupDevicesApiPath(groupId), data)
    .catch(e => e.response as AxiosError<ApiError> as never);
};

const groupDeviceApiPath = (id: string, deviceId: string) => `/device_groups/${id}/devices/${deviceId}`;

export const removeDevicesFromGroupAPI = async ({
  groupId,
  deviceId,
}: {
  groupId: string;
  deviceId: string;
}): Promise<AxiosResponse<void & ApiError>> => {
  return instance.delete(groupDeviceApiPath(groupId, deviceId)).catch(e => e.response as AxiosError<ApiError> as never);
};

export const getDevicesNotInGroup = async ({
  groupId,
  params,
}: {
  groupId: string;
  params?: { search?: string };
}): Promise<AxiosResponse<GroupDevice[] & ApiError>> => {
  return instance
    .get(`devices_not_in_group/${groupId}`, { params })
    .catch(e => e.response as AxiosError<ApiError> as never);
};

const groupFirmwaresApiPath = (id: string) => `/device_groups/${id}/firmwares`;

export const getGroupFirmwaresAPI = async ({
  groupId,
  params,
}: {
  groupId: string;
  params?: { search?: string };
}): Promise<AxiosResponse<Firmware[] & ApiError>> => {
  return instance
    .get<Firmware[]>(groupFirmwaresApiPath(groupId), { params })
    .catch(e => e.response as AxiosError<ApiError> as never);
};

type FirmwareIds = {
  firmwareId: number;
};
export const addFirmwareToGroupAPI = async ({
  groupId,
  data,
}: {
  groupId: string;
  data: FirmwareIds;
}): Promise<AxiosResponse<Firmware & ApiError>> => {
  return instance
    .post<Firmware>(groupFirmwaresApiPath(groupId), data)
    .catch(e => e.response as AxiosError<ApiError> as never);
};

const groupFirmwareApiPath = (id: string, firmwareId: number) => `/device_groups/${id}/firmwares/${firmwareId}`;

export const removeFirmwareFromGroupAPI = async ({
  groupId,
  firmwareId,
}: {
  groupId: string;
  firmwareId: number;
}): Promise<AxiosResponse<void & ApiError>> => {
  return instance
    .delete(groupFirmwareApiPath(groupId, firmwareId))
    .catch(e => e.response as AxiosError<ApiError> as never);
};
